import { STEP_COMPONENTS } from './components/steps'
import { Provider, useAtomValue, useSetAtom } from 'jotai'
import { GreyGrey30, Flex } from '@sequencehq/core-components'
import { motion, AnimatePresence } from 'framer-motion'
import { ScopeProvider } from 'jotai-scope'
import { getStepData } from './utils/getStepData'
import invariant from 'tiny-invariant'
import {
  customerDataAdapter,
  billingScheduleAdapter
} from './contractReviewTask.adapters'
import { useNotifications } from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import Spinner from 'components/Loading'
import { useManualTaskProcessorToolkit } from 'Automations/nodeDefinitions/hooks/useManualTaskProcessorToolkit'
import { TaskInput, TaskOutput } from '../../types'
import { ManualTaskLayout } from 'Automations/components'
import { ContractReviewHeader } from '../ContractReviewHeader'
import { useIsMobile } from 'hooks/useIsMobile'
import { ContractPreview } from './components/contractPreview'
import {
  automationExecutionAtom,
  currentStepAtom,
  currentStepIndexAtom,
  goNextStepAtom,
  goPreviousStepAtom,
  isFinalStepAtom,
  stepsAtom,
  StepsMap
} from './atoms'
import { waitForScheduleCreation } from './waitForScheduleCreation'
import { automationsPaths } from 'Automations/router'
import { useNavigate } from 'react-router-dom'

const STEP_WIDTHS = {
  REVIEW_CUSTOMER: '570px', // 522px + 48px padding
  REVIEW_PRICING: '808px' // 760px + 48px padding
} as const

const BaseContractReviewTask = ({
  onComplete,
  automationExecutionId,
  taskInput
}: {
  onComplete: (output: TaskOutput) => Promise<void>
  automationExecutionId: string
  taskInput: TaskInput
}) => {
  const currentStep = useAtomValue(currentStepAtom)
  const steps = useAtomValue(stepsAtom)
  const goNextStep = useSetAtom(goNextStepAtom)
  const goPreviousStep = useSetAtom(goPreviousStepAtom)
  const isFinalStep = useAtomValue(isFinalStepAtom)
  const { displayNotification } = useNotifications()
  const isMobile = useIsMobile()
  const navigate = useNavigate()

  const assetId = taskInput.assetId
  invariant(assetId, 'Contract asset not provided for task')

  if (!currentStep) {
    return <div>Loading...</div>
  }

  const completeTask = async () => {
    const customerOutput = await customerDataAdapter.toOutput(
      getStepData(steps)('REVIEW_CUSTOMER')
    )

    const pricingOutput = await billingScheduleAdapter.toOutput(
      getStepData(steps)('REVIEW_PRICING')
    )

    await onComplete({
      customerId: customerOutput,
      billingSchedule: pricingOutput
    })

    const { billingScheduleId } = await waitForScheduleCreation(
      automationExecutionId
    )

    // A schedule is only created once the downstream node runs, so fall back to
    // the workflow itself when the wait above times out without one.
    const view = billingScheduleId
      ? {
          label: 'View schedule',
          to: `/billing-schedules/${billingScheduleId}`
        }
      : {
          label: 'View workflow',
          to: automationsPaths.executionHistoryPreview(automationExecutionId)
        }

    displayNotification('Review completed', {
      type: 'success',
      viewLabel: view.label,
      onClickView: () => navigate(view.to),
      'data-testid': 'contractReview.toast'
    })
  }

  const StepComponent = STEP_COMPONENTS[currentStep]

  if (!StepComponent) {
    return null
  }

  const desktopStepWidth = currentStep ? STEP_WIDTHS[currentStep] : '570px'
  // On mobile this pane is shown alone (the PDF preview is dropped) in a
  // full-width container, so the fixed step widths would overflow the viewport
  // with no horizontal scroll — fill the width instead.
  const currentStepWidth = isMobile ? '100%' : desktopStepWidth

  return (
    <motion.div
      style={{
        borderLeft: `1px solid ${GreyGrey30}`,
        overflow: 'hidden',
        height: '100%'
      }}
      initial={{ width: currentStepWidth }}
      animate={{ width: currentStepWidth }}
      transition={{ duration: 0.35 }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          style={{ height: '100%', width: '100%' }}
        >
          <StepComponent
            onNext={async () => {
              if (isFinalStep) {
                await completeTask()
              } else {
                goNextStep()
              }
            }}
            onBack={goPreviousStep}
          />
        </motion.div>
      </AnimatePresence>
    </motion.div>
  )
}

/**
 * Contract review specific adapters.
 */
export const ContractReviewContent = ({ taskId }: { taskId: string }) => {
  const { manualTask, isPending, handleClose, completeTask } =
    useManualTaskProcessorToolkit<TaskInput>({
      taskId
    })
  const merchantSettings = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )

  if (isPending || merchantSettings.isPending) {
    return <ManualTaskLayout.Loading />
  }

  if (!manualTask?.input) {
    return <ManualTaskLayout.Error />
  }

  if (merchantSettings.isPending) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100%">
        <Spinner />
      </Flex>
    )
  }

  return (
    <Provider>
      <ScopeProvider
        atoms={[
          [
            stepsAtom,
            new StepsMap([
              [
                'REVIEW_CUSTOMER',
                customerDataAdapter.fromInput(manualTask.input)
              ],
              [
                'REVIEW_PRICING',
                billingScheduleAdapter.fromInput(manualTask.input, {
                  merchantDefaultDueDateDays:
                    merchantSettings.data?.defaultDueDateDays
                })
              ]
            ])
          ],
          [currentStepIndexAtom, 0],
          [automationExecutionAtom, manualTask.executorWorkflowExecutionId]
        ]}
      >
        <ManualTaskLayout
          header={
            <ManualTaskLayout.Header
              taskId={taskId}
              title={
                <ContractReviewHeader
                  customer={manualTask.input.customer}
                  contractName={manualTask.input.contract?.name}
                />
              }
              onClose={handleClose}
            />
          }
          content={
            <ManualTaskLayout.Content
              // On mobile keep the review form and drop the contract PDF preview.
              mobilePrimary="review"
              subject={
                <ContractPreview
                  assetId={manualTask.input.assetId}
                  contractFields={[]}
                  annotationFieldPathMask={''}
                />
              }
              review={
                <BaseContractReviewTask
                  onComplete={async output => {
                    await completeTask(output)
                  }}
                  taskInput={manualTask.input}
                  automationExecutionId={manualTask.executorWorkflowExecutionId}
                />
              }
            />
          }
        />
      </ScopeProvider>
    </Provider>
  )
}
